import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Text,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import { ChevronLeft20Regular, ChevronRight20Regular } from "@fluentui/react-icons";
import { formatLongDate, parseISODate, toISODate, todayISO } from "../../utils/dateUtils";

// Month grid where any number of individual days can be toggled on/off (up to
// `maxSelections`), so a 2-day request can be Monday + Wednesday. Which days are
// pickable is decided by the caller via `getUnavailableReason`.

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalS, maxWidth: "420px" },
  disabled: { opacity: 0.45, pointerEvents: "none" },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, minmax(36px, 1fr))",
    gap: tokens.spacingHorizontalXXS,
  },
  weekday: {
    textAlign: "center",
    color: tokens.colorNeutralForeground3,
    paddingBottom: tokens.spacingVerticalXXS,
  },
  cell: {
    position: "relative",
    height: "36px",
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground1,
    fontFamily: "inherit",
    fontSize: tokens.fontSizeBase300,
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorSubtleBackgroundHover },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "1px" },
  },
  today: { boxShadow: `inset 0 0 0 1px ${tokens.colorBrandStroke1}` },
  selected: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    fontWeight: tokens.fontWeightSemibold,
    ":hover": { backgroundColor: tokens.colorBrandBackgroundHover },
  },
  unavailable: {
    color: tokens.colorNeutralForegroundDisabled,
    cursor: "not-allowed",
    ":hover": { backgroundColor: "transparent" },
  },
  booked: {
    backgroundColor: tokens.colorStatusWarningBackground2,
    color: tokens.colorStatusWarningForeground2,
    ":hover": { backgroundColor: tokens.colorStatusWarningBackground2 },
  },
  holiday: {
    "::after": {
      content: '""',
      position: "absolute",
      bottom: "4px",
      left: "50%",
      width: "4px",
      height: "4px",
      marginLeft: "-2px",
      borderRadius: "50%",
      backgroundColor: tokens.colorPaletteRedForeground1,
    },
  },
  full: { opacity: 0.5, cursor: "default", ":hover": { backgroundColor: "transparent" } },
  legend: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalM,
    color: tokens.colorNeutralForeground3,
  },
  legendItem: { display: "inline-flex", alignItems: "center", gap: tokens.spacingHorizontalXS },
  swatch: { width: "12px", height: "12px", borderRadius: tokens.borderRadiusSmall },
  dot: { width: "6px", height: "6px", borderRadius: "50%", backgroundColor: tokens.colorPaletteRedForeground1 },
});

export interface LeaveDatePickerProps {
  /** Selected dates, sorted YYYY-MM-DD. */
  selected: string[];
  maxSelections: number;
  /** Any YYYY-MM-DD in the month to show; the view jumps when this changes. */
  initialMonth: string;
  /** No navigating to months before the one containing this date. */
  minDate?: string;
  /** Why a date can't be picked (shown on hover), or null if it can. */
  getUnavailableReason: (iso: string) => string | null;
  disabled?: boolean;
  onChange: (dates: string[]) => void;
}

const monthStart = (iso: string) => `${iso.slice(0, 7)}-01`;

function shiftMonth(firstOfMonth: string, delta: number): string {
  const d = parseISODate(firstOfMonth);
  return toISODate(new Date(d.getFullYear(), d.getMonth() + delta, 1));
}

export function LeaveDatePicker({
  selected,
  maxSelections,
  initialMonth,
  minDate,
  getUnavailableReason,
  disabled,
  onChange,
}: LeaveDatePickerProps) {
  const s = useStyles();
  const today = todayISO();
  const [month, setMonth] = useState(monthStart(initialMonth));

  useEffect(() => setMonth(monthStart(initialMonth)), [initialMonth]);

  const minMonth = monthStart(minDate ?? today);
  const maxMonth = shiftMonth(monthStart(today), 12);
  const full = selected.length >= maxSelections;

  const cells = useMemo(() => {
    const first = parseISODate(month);
    const blanks = first.getDay();
    const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
    const out: (string | null)[] = Array(blanks).fill(null);
    for (let day = 1; day <= daysInMonth; day++) {
      out.push(toISODate(new Date(first.getFullYear(), first.getMonth(), day)));
    }
    return out;
  }, [month]);

  function toggle(iso: string, reason: string | null) {
    if (selected.includes(iso)) {
      onChange(selected.filter((d) => d !== iso));
    } else if (!reason && !full) {
      onChange([...selected, iso].sort());
    }
  }

  const monthLabel = parseISODate(month).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  return (
    <div className={mergeClasses(s.root, disabled && s.disabled)} aria-disabled={disabled}>
      <div className={s.header}>
        <Button
          appearance="subtle"
          icon={<ChevronLeft20Regular />}
          aria-label="Previous month"
          disabled={disabled || month <= minMonth}
          onClick={() => setMonth(shiftMonth(month, -1))}
        />
        <Text weight="semibold" aria-live="polite">
          {monthLabel}
        </Text>
        <Button
          appearance="subtle"
          icon={<ChevronRight20Regular />}
          aria-label="Next month"
          disabled={disabled || month >= maxMonth}
          onClick={() => setMonth(shiftMonth(month, 1))}
        />
      </div>

      <div className={s.grid} role="group" aria-label={`Leave dates, ${monthLabel}`}>
        {WEEKDAYS.map((w) => (
          <Text key={w} size={200} className={s.weekday}>
            {w}
          </Text>
        ))}
        {cells.map((iso, i) => {
          if (!iso) return <span key={`blank-${i}`} />;
          const isSelected = selected.includes(iso);
          const reason = getUnavailableReason(iso);
          const isBooked = !!reason?.startsWith("Already filed");
          const isHoliday = !!reason?.startsWith("Holiday");
          const isFull = !isSelected && !reason && full;
          const title = isSelected
            ? "Selected. Click to unselect."
            : reason ?? (isFull ? `You've picked all ${maxSelections} day(s). Unselect one to change.` : undefined);
          return (
            <button
              key={iso}
              type="button"
              className={mergeClasses(
                s.cell,
                iso === today && s.today,
                !!reason && !isSelected && s.unavailable,
                isBooked && s.booked,
                isHoliday && s.holiday,
                isFull && s.full,
                isSelected && s.selected,
              )}
              aria-pressed={isSelected}
              aria-disabled={(!!reason || isFull) && !isSelected}
              aria-label={`${formatLongDate(iso)}${title ? `. ${title}` : ""}`}
              title={title}
              tabIndex={disabled ? -1 : 0}
              onClick={() => toggle(iso, reason)}
            >
              {parseISODate(iso).getDate()}
            </button>
          );
        })}
      </div>

      <div className={s.legend}>
        <span className={s.legendItem}>
          <span className={s.swatch} style={{ backgroundColor: tokens.colorBrandBackground }} />
          <Text size={200}>Selected</Text>
        </span>
        <span className={s.legendItem}>
          <span className={s.swatch} style={{ backgroundColor: tokens.colorStatusWarningBackground2 }} />
          <Text size={200}>Already filed</Text>
        </span>
        <span className={s.legendItem}>
          <span className={s.dot} />
          <Text size={200}>Holiday</Text>
        </span>
        <Text size={200}>Greyed out: weekend or outside the filing window</Text>
      </div>
    </div>
  );
}
